import { AuthGate } from '../auth/AuthGate';
import { ServiceScreen } from '../ui/ServiceScreen';
import { useServiceState } from './useServiceState';

/**
 * The outermost boundary of the client: may this browser run the game at all?
 *
 * ## Above the session, not beside it
 *
 * `AuthGate` decides WHO is playing; this decides WHETHER anything plays. It
 * has to be the outer one, because during maintenance the answer for a visitor
 * with no session and for a player mid-hunt is the same screen - and because
 * asking "who are you" first would mean an interactive sign-in exists on screen
 * while the front door is shut.
 *
 * Nothing below it is mounted until the server has said it is serving: no
 * Phaser, no hunt, no inventory, no market, no store, no sign-in form.
 *
 * ## Coming back is a REMOUNT, never a resume
 *
 * `generation` changes when a probe finds the service serving again, and it is
 * the `key` of the subtree. React therefore throws the old tree away and builds
 * a new one: a new `AuthGate` re-validates the session, a new `App` builds a new
 * bridge, and `useHuntSession` bootstraps from the server.
 *
 * That is the point. Everything from before the outage is suspect - a snapshot
 * from a simulation the server may have reset, a balance from a wallet it may
 * have rewritten, an inventory it may have emptied - and resuming any of it
 * would put a player back into a run that no longer exists, showing numbers
 * that are no longer true.
 */
export function AvailabilityGate() {
  const service = useServiceState();

  if (service.state === null) {
    // The first probe has not answered. Deliberately nothing but a line of
    // text: building the game and then discovering it may not run is the
    // failure this gate exists to prevent.
    return (
      <div className="service-screen service-screen--probing" data-testid="service-probing">
        <p className="service-screen__body">Verificando servidor…</p>
      </div>
    );
  }

  if (service.state !== 'serving') {
    return (
      <ServiceScreen
        state={service.state}
        checking={service.checking}
        checkedAt={service.checkedAt}
        onRecheck={service.recheck}
      />
    );
  }

  return <AuthGate key={service.generation} />;
}
